import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,  
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
// Category icons (MUI)
import WebIcon from "@mui/icons-material/Web";
import DeveloperModeIcon from "@mui/icons-material/DeveloperMode";
import StorageIcon from "@mui/icons-material/Storage";
import CloudIcon from "@mui/icons-material/Cloud";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BugReportIcon from "@mui/icons-material/BugReport";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import BuildIcon from "@mui/icons-material/Build";
import VerifiedIcon from "@mui/icons-material/Verified";

// Tech “chip” icons (MUI)
import JavascriptIcon from "@mui/icons-material/Javascript";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import DataObjectIcon from "@mui/icons-material/DataObject";
import HubIcon from "@mui/icons-material/Hub";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import TerminalIcon from "@mui/icons-material/Terminal";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ScienceIcon from "@mui/icons-material/Science";

type TechItem = {
  label: string;
  icon?: React.ReactElement;
};

type TechSection = {
  title: string;
  tag: string;
  icon: React.ReactElement;
  note: string;
  items: TechItem[];
};

const techSections: TechSection[] = [
  {
    title: "Frontend",
    tag: "Web UI",
    icon: <WebIcon />,
    note: "Modern, accessible UI built with performance and maintainability in mind.",
    items: [
      { label: "React", icon: <DataObjectIcon fontSize="small" /> },
      { label: "Angular", icon: <DeveloperModeIcon fontSize="small" /> },
      { label: "TypeScript", icon: <IntegrationInstructionsIcon fontSize="small" /> },
      { label: "JavaScript", icon: <JavascriptIcon fontSize="small" /> },
      { label: "Material UI (MUI)", icon: <BuildIcon fontSize="small" /> },
      { label: "Responsive UX", icon: <SpeedIcon fontSize="small" /> },
    ],
  },
  {
    title: "Backend",
    tag: "APIs",
    icon: <DeveloperModeIcon />,
    note: "Clean, secure APIs that scale with your product usage and growth.",
    items: [
      { label: "Node.js", icon: <TerminalIcon fontSize="small" /> },
      { label: "REST APIs", icon: <HubIcon fontSize="small" /> },
      { label: "Microservices", icon: <HubIcon fontSize="small" /> },
      { label: "Auth patterns (JWT/OAuth)", icon: <SecurityIcon fontSize="small" /> },
      { label: "Caching patterns", icon: <SpeedIcon fontSize="small" /> },
    ],
  },
  {
    title: "Databases & Caching",
    tag: "Data",
    icon: <StorageIcon />,
    note: "Right datastore for the workload, tuned for real-world performance.",
    items: [
      { label: "MongoDB", icon: <StorageOutlinedIcon fontSize="small" /> },
      { label: "MySQL", icon: <StorageOutlinedIcon fontSize="small" /> },
      { label: "Redis", icon: <SpeedIcon fontSize="small" /> },
      { label: "Schema design", icon: <BuildIcon fontSize="small" /> },
      { label: "Indexing & tuning", icon: <SpeedIcon fontSize="small" /> },
    ],
  },
  {
    title: "Cloud & Hosting (AWS)",
    tag: "Cloud",
    icon: <CloudIcon />,
    note: "Secure global delivery with HTTPS, CDN, and strong access controls.",
    items: [
      { label: "AWS Cloud", icon: <CloudIcon fontSize="small" /> },
      { label: "S3 Static Hosting", icon: <Inventory2Icon fontSize="small" /> },
      { label: "CloudFront (CDN + HTTPS)", icon: <RocketLaunchIcon fontSize="small" /> },
      { label: "Route 53 (DNS)", icon: <HubIcon fontSize="small" /> },
      { label: "ACM Certificates (TLS/SSL)", icon: <VerifiedIcon fontSize="small" /> },
      { label: "IAM & Security", icon: <SecurityIcon fontSize="small" /> },
    ],
  },
  {
    title: "Containerization & Orchestration",
    tag: "DevOps",
    icon: <Inventory2Icon />,
    note: "Repeatable deployments and scaling with container-first engineering.",
    items: [
      { label: "Docker", icon: <Inventory2Icon fontSize="small" /> },
      { label: "Kubernetes", icon: <HubIcon fontSize="small" /> },
      { label: "Container images", icon: <Inventory2Icon fontSize="small" /> },
      { label: "Environment isolation", icon: <SecurityIcon fontSize="small" /> },
      { label: "Scaling strategies", icon: <SpeedIcon fontSize="small" /> },
    ],
  },
  {
    title: "CI/CD & Automation",
    tag: "Delivery",
    icon: <RocketLaunchIcon />,
    note: "Fast releases with safe automation and consistent pipelines.",
    items: [
      { label: "CI pipelines", icon: <RocketLaunchIcon fontSize="small" /> },
      { label: "Automated build & deploy", icon: <RocketLaunchIcon fontSize="small" /> },
      { label: "Infrastructure automation", icon: <BuildIcon fontSize="small" /> },
      { label: "Versioning & release strategy", icon: <VerifiedIcon fontSize="small" /> },
    ],
  },
  {
    title: "Testing & QA",
    tag: "Quality",
    icon: <BugReportIcon />,
    note: "Quality gates to prevent regressions while keeping delivery fast.",
    items: [
      { label: "Unit testing", icon: <ScienceIcon fontSize="small" /> },
      { label: "Integration testing", icon: <ScienceIcon fontSize="small" /> },
      { label: "E2E testing", icon: <BugReportIcon fontSize="small" /> },
      { label: "Code reviews", icon: <VerifiedIcon fontSize="small" /> },
      { label: "Static analysis (linting)", icon: <BuildIcon fontSize="small" /> },
      { label: "Performance checks", icon: <SpeedIcon fontSize="small" /> },
    ],
  },
  {
    title: "Observability",
    tag: "Reliability",
    icon: <VisibilityIcon />,
    note: "Monitor, debug, and improve production systems with clear visibility.",
    items: [
      { label: "Logging", icon: <VisibilityIcon fontSize="small" /> },
      { label: "Metrics", icon: <SpeedIcon fontSize="small" /> },
      { label: "Monitoring dashboards", icon: <VisibilityIcon fontSize="small" /> },
      { label: "Error tracking", icon: <BugReportIcon fontSize="small" /> },
      { label: "Alerting patterns", icon: <SecurityIcon fontSize="small" /> },
    ],
  },
  {
    title: "AI & Assistants",
    tag: "AI",
    icon: <AutoAwesomeIcon />,
    note: "Practical AI features integrated into products with guardrails and control.",
    items: [
      { label: "ChatGPT (OpenAI)", icon: <PsychologyIcon fontSize="small" /> },
      { label: "Gemini (Google)", icon: <PsychologyIcon fontSize="small" /> },
      { label: "Prompting best practices", icon: <AutoAwesomeIcon fontSize="small" /> },
      { label: "RAG (knowledge Q&A)", icon: <HubIcon fontSize="small" /> },
      { label: "Guardrails & safety", icon: <SecurityIcon fontSize="small" /> },
    ],
  },
];

const qualityPillars = [
  { title: "Security-first", icon: <SecurityIcon />, desc: "Least-privilege access, secure defaults, and HTTPS everywhere." },
  { title: "Performance", icon: <SpeedIcon />, desc: "Caching, CDN delivery, and efficient database access patterns." },
  { title: "Maintainability", icon: <BuildIcon />, desc: "Clean architecture, code reviews, and strong conventions." },
  { title: "Reliability", icon: <VerifiedIcon />, desc: "Monitoring, alerting, and controlled releases." },
];

const Technologies = () => {
  return (
    <Box component="main">
      {/* HERO */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", maxWidth: 900, mx: "auto" }}>
            <Typography variant="h4" component="h1" fontWeight={900} gutterBottom>
              Technologies
            </Typography>
            <Typography variant="h6" color="text.secondary">
              A modern stack for building cloud-native education products—fast, reliable, and scalable.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
              <Button variant="contained" component={RouterLink} to="/contact-us">
                Discuss your project
              </Button>
              <Button variant="outlined" component={RouterLink} to="/products">
                View Products
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* TECH STACK */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
            Our core stack
          </Typography>
          <Typography color="text.secondary">
            Technology choices that help deliver a strong MVP and scale confidently.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {techSections.map((sec) => (
            <Grid item xs={12} md={4} key={sec.title}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <Box sx={{ display: "inline-flex", alignItems: "center" }}>{sec.icon}</Box>
                    <Typography variant="h6" fontWeight={800} sx={{ flexGrow: 1 }}>
                      {sec.title}
                    </Typography>
                    <Chip size="small" label={sec.tag} />
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {sec.note}
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {sec.items.map((t) => (
                      <Chip
                        key={t.label}
                        label={t.label}
                        size="small"
                        variant="outlined"
                        icon={t.icon}
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* QUALITY */}
      <Box sx={{ py: { xs: 5, md: 7 }, bgcolor: "action.hover" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
              Testing & Quality Assurance
            </Typography>
            <Typography color="text.secondary">
              Quality gates that keep delivery fast while reducing production risk.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {qualityPillars.map((p) => (
              <Grid item xs={12} md={3} key={p.title}>
                <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                      <Box sx={{ display: "inline-flex", alignItems: "center" }}>{p.icon}</Box>
                      <Typography variant="h6" fontWeight={800}>
                        {p.title}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {p.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            textAlign: "center",
          }}
        >
          <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
            Want this stack for your education product?
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            We can deliver an MVP quickly and scale it with cloud-native architecture.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
            <Button variant="contained" size="large" component={RouterLink} to="/contact-us">
              Contact Us
            </Button>
            <Button variant="outlined" size="large" component={RouterLink} to="/services">
              View Services
            </Button>
          </Stack>
        </Card>
      </Container>
    </Box>
  );
};

export default Technologies;
